import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import {
  classificationChanges,
  currentPick,
  typesFor,
  withCategory,
  type ClassificationPick
} from "../classification";
import type { ClassificationChange } from "../hooks/useCaseFilesystem";
import { useCaseTaxonomy } from "../hooks/useCaseTaxonomy";
import { nodeFilename, nodeTitle } from "../nodeNames";
import type { FsNode, TaxonomyCategory } from "../types";

const NONE = "__none__";

type Props = {
  individualId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The files to classify; folders are ignored. */
  nodes: FsNode[];
  onSave: (changes: ClassificationChange[]) => Promise<void>;
  onSaved?: () => void;
};

/**
 * A file's category and document type selects. `labeled` titles each one
 * (Category, Type) for a place without the dialog's row layout.
 */
export const PickSelects: FC<{
  categories: TaxonomyCategory[];
  pick: ClassificationPick;
  onChange: (pick: ClassificationPick) => void;
  label: string;
  labeled?: boolean;
}> = ({ categories, pick, onChange, label, labeled = false }) => {
  const types = typesFor(categories, pick.category);
  const unknownCategory =
    pick.category && !categories.some((row) => row.category === pick.category)
      ? pick.category
      : null;
  const unknownType =
    pick.documentType && !types.includes(pick.documentType)
      ? pick.documentType
      : null;
  const titled = (title: string, select: ReactNode) =>
    labeled ? (
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm text-fg-2">{title}</span>
        {select}
      </div>
    ) : (
      select
    );
  return (
    <div
      className={
        labeled
          ? "flex flex-wrap items-start gap-3"
          : "flex shrink-0 items-center gap-2"
      }
    >
      {titled(
        "Category",
        <Select
          value={pick.category ?? NONE}
          onValueChange={(value) =>
            onChange(
              withCategory(categories, pick, value === NONE ? null : value)
            )
          }
        >
          <SelectTrigger
            size="sm"
            className="w-56"
            aria-label={`Category for ${label}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={NONE}>No Category</SelectItem>
            {unknownCategory ? (
              <SelectItem value={unknownCategory}>{unknownCategory}</SelectItem>
            ) : null}
            {categories.map((row) => (
              <SelectItem key={row.category} value={row.category}>
                <span className="truncate">{row.category}</span>
                {row.internal ? (
                  <span className="text-sm text-fg-4">Internal</span>
                ) : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {titled(
        "Type",
        <Select
          value={pick.documentType ?? NONE}
          disabled={!pick.category || (!types.length && !unknownType)}
          onValueChange={(value) =>
            onChange({ ...pick, documentType: value === NONE ? null : value })
          }
        >
          <SelectTrigger
            size="sm"
            className="w-48"
            aria-label={`Document type for ${label}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={NONE}>Not Set</SelectItem>
            {unknownType ? (
              <SelectItem value={unknownType}>{unknownType}</SelectItem>
            ) : null}
            {types.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

/**
 * Sets or corrects what case files are, in the visa's taxonomy: the category
 * and document type Classic drafting, research and suggestions read. An
 * attorney's choice is never changed by a later organizer run.
 */
export const ClassifyFilesDialog: FC<Props> = ({
  individualId,
  open,
  onOpenChange,
  nodes,
  onSave,
  onSaved
}) => {
  const files = useMemo(
    () => nodes.filter((n) => n.kind === "file" && !n.is_deleted),
    [nodes]
  );
  const taxonomy = useCaseTaxonomy(individualId, open);
  const categories = taxonomy.data?.categories ?? [];
  const [picks, setPicks] = useState<Record<string, ClassificationPick>>({});
  const [all, setAll] = useState<ClassificationPick>({
    category: null,
    documentType: null
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicks(Object.fromEntries(files.map((n) => [n.id, currentPick(n)])));
    setAll({ category: null, documentType: null });
    // Not on `files`: a tree refresh must not undo the attorney's picks.
  }, [open]);

  const changes = useMemo(
    () => classificationChanges(files, picks),
    [files, picks]
  );

  const save = async () => {
    if (!changes.length) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(changes);
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't save the categories."
      );
    } finally {
      setSaving(false);
    }
  };

  const setEvery = (pick: ClassificationPick) => {
    setAll(pick);
    setPicks(Object.fromEntries(files.map((n) => [n.id, pick])));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-160 flex-col sm:max-w-200">
        <DialogHeader>
          <DialogTitle>Category and Type</DialogTitle>
          <DialogDescription>
            What each file is in this case's filing categories. Drafting,
            research and suggestions read it, and the organizer won't change a
            category you set.
          </DialogDescription>
        </DialogHeader>
        {taxonomy.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : taxonomy.isError ? (
          <span className="py-6 text-center text-sm text-danger">
            Couldn't load this case's categories.
          </span>
        ) : !categories.length ? (
          <span className="py-6 text-center text-sm text-fg-3">
            This case's visa has no filing categories, so its files can't be
            categorized.
          </span>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-px">
            {files.length > 1 ? (
              <ListCard>
                <ListRow
                  title="Set All"
                  description={`Gives all ${files.length} files one category and type.`}
                  control={
                    <PickSelects
                      categories={categories}
                      pick={all}
                      onChange={setEvery}
                      label="All Files"
                    />
                  }
                />
              </ListCard>
            ) : null}
            <ListCard>
              {files.map((n) => (
                <ListRow
                  key={n.id}
                  media={<FileTypeIcon name={nodeFilename(n)} />}
                  title={<span className="truncate">{nodeTitle(n)}</span>}
                  control={
                    <PickSelects
                      categories={categories}
                      pick={picks[n.id] ?? currentPick(n)}
                      onChange={(pick) =>
                        setPicks((current) => ({ ...current, [n.id]: pick }))
                      }
                      label={nodeTitle(n)}
                    />
                  }
                />
              ))}
            </ListCard>
          </div>
        )}
        <DialogFooter className="items-center">
          <span className="flex-1 text-sm text-fg-3">
            {changes.length
              ? `${changes.reduce((n, c) => n + c.nodeIds.length, 0)} Changed`
              : "No Changes"}
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving || !categories.length}
            onClick={() => void save()}
          >
            {saving ? <Spinner className="size-3.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
